import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TradingContext } from '../contexts/TradingContext';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import { usePerformanceData } from '../hooks/usePerformanceData';
import { PusherContext } from '../providers/PusherProvider';
import { Header } from '../components/Header';
import useIsMobile from '../hooks/useIsMobile';
import useTradingRulesMetrics from '../hooks/useTradingRulesMetrics';
import { useTradingRulesConfig } from '../contexts/TradingRulesConfigContext';
import { useRealtimeAccountMetrics } from '../hooks/useRealtimeAccountMetrics';
import TradesUsedTodayModal from '../components/TradingRules/TradesUsedTodayModal';
import JournalDayModal from '../components/TradingRules/JournalDayModal';
import DashboardPageHeader from '../components/shared/DashboardPageHeader';
import DashboardSectionTitle from '../components/shared/DashboardSectionTitle';
import ConstraintBand from '../components/TradingRules/ConstraintBand';
import ViolationsOverTimeChart from '../components/TradingRules/charts/ViolationsOverTimeChart';
import ViolationPatternSummary from '../components/TradingRules/metrics/ViolationPatternSummary';
import RuleBreakCostsSection from '../components/TradingRules/metrics/RuleBreakCostsSection';
import TradingRulesSettingsModal from '../components/TradingRules/TradingRulesSettingsModal';
import { resolveTradingRulesMockScenario, resolveTodayDemoScenario, getTodayDemoOverrides } from '../utils/tradingRulesMockScenario';
import BrokerNotConnectedState from '../components/shared/BrokerNotConnectedState';
import { needsBrokerConnection } from '../utils/onboardingStatus';
import useTradingLockout from '../hooks/useTradingLockout';
import PerformanceTimeframeBar, { TIMEFRAME_PRESETS } from '../components/Performance/PerformanceTimeframeBar';
import { getDashboardPageShellStyle } from '../constants/dashboardLayout';
import { formatLockoutExpiresAt } from '../utils/lockoutTimeFormat';

/**
 * TradingRulesDashboard - Main page for trading rules compliance dashboard
 * 
 * Displays:
 * - Constraint band (4 critical metrics)
 * - Secondary metrics (modification rate, breach history)
 * 
 * Requires user to configure settings before viewing dashboard
 */
const TradingRulesDashboard = ({
  hideHeader = false,
  hideDashboardHeader = false,
  hideAccountDropdown = false,
  dashboardTitle,
  hideSnapshotTradingRulesButton = false,
  /** Parent already applies MOBILE_UI.pagePadding (Portfolio Health tab) — zero the shell pad. */
  embeddedMobile = false,
}) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedAccount, isLoading: accountsLoading } = useContext(TradingContext);
  const { user } = useContext(AuthContext);
  const pusher = useContext(PusherContext);
  const { isTradingLocked, lockoutExpiresAt } = useTradingLockout();
  const lockoutEndsText = useMemo(() => {
    if (!lockoutExpiresAt) return null;
    return formatLockoutExpiresAt(lockoutExpiresAt);
  }, [lockoutExpiresAt]);
  
  // Memoize account to prevent unnecessary re-renders if account object reference changes
  const accountId = useMemo(() => selectedAccount?.AccountID, [selectedAccount?.AccountID]);
  const stableAccount = useMemo(() => {
    if (!selectedAccount || !accountId) return null;
    return { AccountID: accountId };
  }, [accountId, selectedAccount]);
  
  // Load timeRange from localStorage, default to 'month'
  const [timeRange, setTimeRange] = useState(() => {
    const saved = localStorage.getItem('trading_rules_timeRange');
    if (!saved) return 'quarter';
    // Migrate legacy ids
    if (saved === '7days') return 'week';
    if (saved === '30days') return 'month';
    if (TIMEFRAME_PRESETS.some((p) => p.id === saved)) return saved;
    return 'quarter';
  });
  
  const [showSettings, setShowSettings] = useState(false);
  const [journalDayModal, setJournalDayModal] = useState(null);
  const [showTradesUsedModal, setShowTradesUsedModal] = useState(false);
  const todayEtDateStr = useMemo(
    () => new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date()),
    []
  );
  
  // Save timeRange to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('trading_rules_timeRange', timeRange);
  }, [timeRange]);

  // Check if settings exist for this account
  const { 
    settings, 
    loading: settingsLoading, 
    hasSettings,
    saveSettings
  } = useTradingRulesConfig();

  // Today's snapshot — always fetched (live session limits)
  const {
    metrics: todayMetrics,
    violations: todayViolations,
    breaches: todayBreaches,
    journalCompliance: todayJournalComplianceFromHook,
    loading: todayMetricsLoading,
    error: todayError,
    refetch: refetchTodayMetrics,
  } = useTradingRulesMetrics(stableAccount, 'today');

  // Period metrics — driven by Today / 7D / 30D filter (skip duplicate fetch when already loading today)
  const shouldFetchPeriodSeparately = timeRange !== 'today';
  const { 
    metrics: periodMetrics,
    violations: periodViolations,
    breaches: periodBreaches,
    loading: timeFrameMetricsLoading, 
    error: timeFrameError,
    refetch: refetchTimeFrameMetrics
  } = useTradingRulesMetrics(stableAccount, shouldFetchPeriodSeparately ? timeRange : null);

  const violations = timeRange === 'today' ? todayViolations : periodViolations;
  const breaches = timeRange === 'today' ? todayBreaches : periodBreaches;
  const periodCostMetrics = timeRange === 'today' ? todayMetrics : periodMetrics;

  const [showPlanDeviations, setShowPlanDeviations] = useState(false);
  const patternsRef = useRef(null);

  // Callback to refetch all metrics after settings change.
  // The chart reads these violations, so it does not fetch them again.
  const refetchAllMetrics = useCallback(() => {
    refetchTodayMetrics();
    refetchTimeFrameMetrics();
  }, [refetchTodayMetrics, refetchTimeFrameMetrics]);

  // Listen for journal creation/update events to refresh metrics
  useEffect(() => {
    const handleJournalChange = () => {
      console.log('[TradingRulesDashboard] Journal created/updated, refreshing metrics...');
      refetchAllMetrics();
    };
    
    window.addEventListener('tradeJournal:created', handleJournalChange);
    window.addEventListener('tradeJournal:updated', handleJournalChange);
    
    return () => {
      window.removeEventListener('tradeJournal:created', handleJournalChange);
      window.removeEventListener('tradeJournal:updated', handleJournalChange);
    };
  }, [refetchAllMetrics]);

  // Listen for tracking-mode violations via Pusher and refresh violation counts
  // Debounce to prevent constant refetches when violations are recorded frequently
  useEffect(() => {
    if (!pusher || !user?.id) return;

    const channelName = `user-${user.id}-alerts`;
    let channel = pusher.channel(channelName);
    if (!channel) {
      channel = pusher.subscribe(channelName);
    }

    let debounceTimer = null;
    const DEBOUNCE_MS = 5000; // Wait 5 seconds before refetching after a violation

    const handleViolationRecorded = () => {
      console.log('[TradingRulesDashboard] Violation recorded, scheduling metrics refresh...');
      
      // Clear existing timer if one is pending
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      // Schedule refetch after debounce period
      debounceTimer = setTimeout(() => {
        console.log('[TradingRulesDashboard] Refreshing metrics after debounce...');
        refetchAllMetrics();
        debounceTimer = null;
      }, DEBOUNCE_MS);
    };

    channel.bind('violation-recorded', handleViolationRecorded);

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (channel) {
        channel.unbind('violation-recorded', handleViolationRecorded);
      }
    };
  }, [pusher, user?.id, refetchAllMetrics]);

  // State for violation type filter in chart
  const [selectedViolationType, setSelectedViolationType] = useState('all');

  // Get real-time today's change (updates live from positions and balances streams)
  // This uses the same logic as PLSummary on the trade page
  const realtimeMetrics = useRealtimeAccountMetrics();

  // Today's performance summary for today's win rate
  const { summary: todayPerformanceSummary } = usePerformanceData({
    timeframe: 'today',
    accountId: accountId || undefined,
    enabled: !!accountId,
  });

  const { useMock: isMockMode } = resolveTradingRulesMockScenario();
  const todayDemoOverrides = useMemo(
    () => getTodayDemoOverrides(resolveTodayDemoScenario(searchParams)),
    [searchParams]
  );
  
  const todayChange = useMemo(() => {
    const todayData = todayMetrics?.metrics || todayMetrics || {};
    const mockDailyLoss = todayData.daily_loss_current ?? 0;
    if (isMockMode && todayMetrics) {
      return mockDailyLoss > 0 ? -mockDailyLoss : mockDailyLoss;
    }
    return realtimeMetrics.todayChange;
  }, [isMockMode, todayMetrics, realtimeMetrics.todayChange]);

  const todayJournalCompliance = useMemo(
    () => todayJournalComplianceFromHook || todayMetrics?.journal_compliance || {},
    [todayJournalComplianceFromHook, todayMetrics]
  );

  const getPeriodLabel = () => {
    switch (timeRange) {
      case 'today': return 'today';
      case '7days':
      case 'week': return 'the last 7 days';
      case '30days':
      case 'month': return 'the last 30 days';
      case 'quarter': return 'the last 90 days';
      case 'ytd': return 'year to date';
      case 'year': return 'the last year';
      case 'all': return 'all time';
      default: return 'this period';
    }
  };

  const metricsLoading = todayMetricsLoading || (shouldFetchPeriodSeparately && timeFrameMetricsLoading);
  const chartLoading = timeRange === 'today' ? todayMetricsLoading : timeFrameMetricsLoading;
  const error = todayError || timeFrameError;
  const brokerLinked = !needsBrokerConnection(user);
  const awaitingAccountSelection = brokerLinked && !stableAccount && accountsLoading;
  // Full-page spinner only for the first load. A timeframe change keeps the page
  // mounted so scroll position stays and the streak pill does not refetch 30 days.
  const [readyAccountId, setReadyAccountId] = useState(null);
  const dashboardReady = !!accountId && readyAccountId === accountId;
  useEffect(() => {
    if (!accountId || settingsLoading || metricsLoading) return;
    setReadyAccountId(accountId);
  }, [accountId, settingsLoading, metricsLoading]);
  const isDashboardLoading =
    awaitingAccountSelection || (!!stableAccount && !dashboardReady && (settingsLoading || metricsLoading));

  const handleTimeframeChange = useCallback(({ timeframe: next }) => {
    const anchor = patternsRef.current;
    let scroller = anchor?.parentElement || null;
    while (scroller) {
      const overflowY = window.getComputedStyle(scroller).overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
        && scroller.scrollHeight > scroller.clientHeight + 1
      ) {
        break;
      }
      scroller = scroller.parentElement;
    }
    const scrollTop = scroller ? scroller.scrollTop : null;
    setTimeRange(next);
    if (!scroller || scrollTop == null) return;
    requestAnimationFrame(() => {
      scroller.scrollTop = scrollTop;
    });
  }, []);

  // If no settings, show placeholder message
  useEffect(() => {
    if (!settingsLoading && !hasSettings) {
      // For now, just show message. Will integrate modal in next step
      console.log('No settings configured for this account');
    }
  }, [settingsLoading, hasSettings]);

  const pageShellStyle = useMemo(() => {
    const base = getDashboardPageShellStyle({ isMobile });
    // Match PerformanceDashboard: when Portfolio (or another shell) already pads the
    // page, drop the dashboard shell padding so cards are not double-inset / squished.
    if (embeddedMobile && isMobile) {
      return { ...base, padding: 0 };
    }
    return base;
  }, [embeddedMobile, isMobile]);

  // No broker connected — show connect card instead of an infinite loading spinner.
  if (user && needsBrokerConnection(user)) {
    return (
      <>
        {!hideHeader && <Header />}
        <Container fluid style={pageShellStyle}>
          {!hideDashboardHeader && (
            <DashboardPageHeader
              title={dashboardTitle || 'Account Health'}
              description="See how closely your trading follows your rules"
            />
          )}
          <BrokerNotConnectedState
            message="Connect your broker to track your trading rules, violations, and account health."
            minHeight={isMobile ? 260 : 320}
          />
        </Container>
      </>
    );
  }

  // Loading state — wait for account only when broker is linked and accounts are still fetching
  if (isDashboardLoading) {
    return (
      <>
        {!hideHeader && <Header />}
        <Container fluid style={pageShellStyle}>
          {/* Desktop-only back button (hidden in dashboard shell — use sidebar) */}
          {!isMobile && !hideHeader && (
            <div className="d-flex align-items-center mb-4">
              <Button
                variant="link"
                onClick={() => navigate('/trade')}
                style={{
                  color: colors.textLight,
                  textDecoration: 'none',
                  padding: '0.5rem',
                  fontSize: '1.2rem',
                  border: 'none',
                  background: 'none'
                }}
                onMouseEnter={(e) => e.target.style.color = colors.primary}
                onMouseLeave={(e) => e.target.style.color = colors.textLight}
              >
                ← Trade
              </Button>
            </div>
          )}
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3" style={{ color: colors.textLight }}>
                Loading dashboard...
              </p>
            </div>
          </div>
        </Container>
      </>
    );
  }

  // Error state — only replace the page before the first successful load.
  // A failed timeframe refresh stays inline so the page does not jump.
  if (error && !dashboardReady) {
    return (
      <>
        {!hideHeader && <Header />}
        <Container fluid style={pageShellStyle}>
          {/* Desktop-only back button (hidden in dashboard shell — use sidebar) */}
          {!isMobile && !hideHeader && (
            <div className="d-flex align-items-center mb-4">
              <Button
                variant="link"
                onClick={() => navigate('/trade')}
                style={{
                  color: colors.textLight,
                  textDecoration: 'none',
                  padding: '0.5rem',
                  fontSize: '1.2rem',
                  border: 'none',
                  background: 'none'
                }}
                onMouseEnter={(e) => e.target.style.color = colors.primary}
                onMouseLeave={(e) => e.target.style.color = colors.textLight}
              >
                ← Trade
              </Button>
            </div>
          )}
          <Alert variant="danger" className="mt-4">
            {error}
          </Alert>
        </Container>
      </>
    );
  }

  // No settings configured — only gate when a broker account is connected
  if (!settingsLoading && !hasSettings && stableAccount) {
    return (
      <>
        {!hideHeader && <Header />}
        <Container fluid style={pageShellStyle}>
          {/* Desktop-only back button (hidden in dashboard shell — use sidebar) */}
          {!isMobile && !hideHeader && (
            <div className="d-flex align-items-center mb-4">
              <Button
                variant="link"
                onClick={() => navigate('/trade')}
                style={{
                  color: colors.textLight,
                  textDecoration: 'none',
                  padding: '0.5rem',
                  fontSize: '1.2rem',
                  border: 'none',
                  background: 'none'
                }}
                onMouseEnter={(e) => e.target.style.color = colors.primary}
                onMouseLeave={(e) => e.target.style.color = colors.textLight}
              >
                ← Trade
              </Button>
            </div>
          )}
          <div style={{ 
            maxWidth: '600px', 
            margin: '0 auto',
            padding: '60px 20px'
          }}>
            <h2 style={{ 
              color: colors.text, 
              marginBottom: '12px',
              fontSize: '2rem',
              fontWeight: 600
            }}>
              Account Health
            </h2>
            <p style={{ 
              color: colors.textLight, 
              marginBottom: '32px', 
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              Configure your trading rule thresholds to start tracking compliance.
            </p>
            
            <div style={{
              backgroundColor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '0.75rem',
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px',
                fontWeight: 500
              }}>
                Required Settings
              </div>
              <div style={{ 
                fontSize: '0.875rem',
                color: colors.textLight,
                lineHeight: '1.8'
              }}>
                <div style={{ marginBottom: '8px' }}>• Max trades per day</div>
                <div style={{ marginBottom: '8px' }}>• Max loss per day</div>
                <div style={{ marginBottom: '8px' }}>• Max loss per position</div>
                <div>Trading time window</div>
              </div>
            </div>
            
            <Button
              variant="primary"
              onClick={() => setShowSettings(true)}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Configure Trading Rules
            </Button>
            
            <p style={{ 
              color: colors.textMuted, 
              fontSize: '0.75rem', 
              marginTop: '16px',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              These settings are for tracking only and won't block trades unless you enable enforcement.
            </p>
          </div>
        </Container>
      </>
    );
  }

  // Main dashboard view
  return (
    <>
      {!hideHeader && <Header />}
      <Container fluid style={pageShellStyle}>
        {/* Desktop-only back button (hidden in dashboard shell — use sidebar) */}
        {!isMobile && !hideHeader && (
          <div className="d-flex align-items-center mb-4">
            <Button
              variant="link"
              onClick={() => navigate('/trade')}
              style={{
                color: colors.textLight,
                textDecoration: 'none',
                padding: '0.5rem',
                fontSize: '1.2rem',
                border: 'none',
                background: 'none'
              }}
              onMouseEnter={(e) => e.target.style.color = colors.primary}
              onMouseLeave={(e) => e.target.style.color = colors.textLight}
            >
              ← Trade
            </Button>
          </div>
        )}
        <DashboardPageHeader
          title={dashboardTitle || 'Account Health'}
          description="See whether you followed your rules today — and what to do next"
          hideTitle={hideDashboardHeader}
        />

        {/* Today answer + snapshot checklist */}
        <ConstraintBand
          todayMetrics={todayMetrics}
          todayChange={todayChange}
          todayJournalCompliance={todayJournalCompliance}
          todayPerformanceSummary={todayPerformanceSummary}
          realtimeMetrics={realtimeMetrics}
          accountId={accountId}
          todayViolations={todayViolations || []}
          isTradingLocked={isTradingLocked}
          lockoutEndsText={lockoutEndsText}
          demoOverrides={todayDemoOverrides}
          onSettingsClick={hideSnapshotTradingRulesButton ? null : () => setShowSettings(true)}
          onJournalMissingClick={() => setJournalDayModal(todayEtDateStr)}
          onTradesClick={() => setShowTradesUsedModal(true)}
        />

        {/* Period patterns — chart + collapsible table */}
        <div ref={patternsRef} style={{ marginTop: '36px', overflowAnchor: 'none' }}>
          <DashboardSectionTitle title="Violations & Patterns">
            <PerformanceTimeframeBar
              timeframe={timeRange}
              dateRange={null}
              onChange={handleTimeframeChange}
              colors={colors}
              isMobile={isMobile}
              align="right"
              showCustom={false}
              emphasis
            />
          </DashboardSectionTitle>

          <div style={{ marginBottom: '12px' }}>
            {error && dashboardReady && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            <ViolationsOverTimeChart
                loading={chartLoading}
                timeRange={timeRange}
                violations={chartLoading ? [] : (violations || [])}
                selectedRuleType={selectedViolationType}
                onFilterChange={setSelectedViolationType}
                accountId={accountId}
                onViolationDeleted={refetchAllMetrics}
                periodLabel={getPeriodLabel()}
                hideTable={false}
                defaultTableExpanded
                showPlanDeviations={showPlanDeviations}
                onShowPlanDeviationsChange={setShowPlanDeviations}
                aside={chartLoading ? null : (
                  <ViolationPatternSummary
                    violations={violations || []}
                    timeRange={timeRange}
                    includePlanDeviations={showPlanDeviations}
                    accountId={accountId}
                  />
                )}
              />
          </div>
        </div>

        <RuleBreakCostsSection
          metrics={periodCostMetrics}
          breaches={breaches || []}
          violations={violations || []}
          timeRange={timeRange}
          periodLabel={getPeriodLabel()}
        />

        {/* Journal Day Modal */}
        <JournalDayModal
          show={!!journalDayModal}
          onHide={() => setJournalDayModal(null)}
          dateStr={journalDayModal}
          accountId={accountId}
          paperTrading={selectedAccount?.paperTrading}
        />

        <TradesUsedTodayModal
          show={showTradesUsedModal}
          onHide={() => setShowTradesUsedModal(false)}
          account={stableAccount}
          tradeDate={todayEtDateStr}
          tradesTaken={
            todayDemoOverrides?.tradesTaken
              ?? todayMetrics?.metrics?.trades_taken
              ?? todayMetrics?.trades_taken
              ?? null
          }
          maxTrades={
            todayDemoOverrides?.maxTrades
              ?? todayMetrics?.metrics?.max_trades
              ?? todayMetrics?.max_trades
              ?? null
          }
        />

        {/* Settings Modal */}
        <TradingRulesSettingsModal
          show={showSettings}
          onHide={() => setShowSettings(false)}
          account={stableAccount}
          settings={settings}
          saveSettings={saveSettings}
          onSettingsSaved={refetchAllMetrics}
        />
      </Container>
    </>
  );
};

export default TradingRulesDashboard;
